import { EDITOR_NAME_PART_KEYS } from '@messenger/lotus'

const APP_NAME_FIELDS: Readonly<Record<string, string>> = {
  prefix: 'prefix_dec',
  first: 'first_dec',
  nickname: 'nick_name_dec',
  middle: 'middle_dec',
  maiden: 'maiden_dec',
  last: 'lastdec',
  suffix: 'suffix_dec',
}

export type FieldSource = {
  key: string
  from: string
  formatted?: boolean
}

export const FIELD_SOURCES: readonly FieldSource[] = [
  ...Object.keys(EDITOR_NAME_PART_KEYS).map(
    (part): FieldSource => ({
      key: EDITOR_NAME_PART_KEYS[part],
      from: APP_NAME_FIELDS[part],
    }),
  ),
  { key: 'date_of_birth', from: 'date_of_birth', formatted: true },
  { key: 'date_of_death', from: 'date_of_death', formatted: true },
  { key: 'year_of_birth', from: 'date_of_birth', formatted: true },
  { key: 'year_of_death', from: 'date_of_death', formatted: true },
  { key: 'first_letter_first', from: 'first_dec', formatted: true },
  { key: 'first_letter_middle', from: 'middle_dec', formatted: true },
  { key: 'first_letter_last', from: 'lastdec', formatted: true },
  { key: 'date_of_marriage', from: 'date_of_marriage', formatted: true },
  { key: 'age_years', from: 'age_years', formatted: true },
  { key: 'age_months', from: 'age_months', formatted: true },
  { key: 'age_days', from: 'age_days', formatted: true },
  { key: 'place_of_birth', from: 'place_of_birth' },
  { key: 'place_of_death', from: 'place_of_death' },
  { key: 'mother_of_deceased', from: 'mother_of_deceased' },
  { key: 'father_of_deceased', from: 'father_of_deceased' },
  { key: 'spouse', from: 'spouse' },
  { key: 'website_obit_url', from: 'website_obit_url' },
  { key: 'obituary', from: 'obituary', formatted: true },
  { key: 'family_appreciation_message', from: 'family_appreciation_message' },
  { key: 'order_of_service', from: 'order_of_service', formatted: true },
  { key: 'interment_place', from: 'interment_place' },
  { key: 'interment_address', from: 'interment_address' },
  { key: 'interment_city', from: 'interment_city' },
  { key: 'interment_state', from: 'interment_state' },
  { key: 'interment_date', from: 'interment_date', formatted: true },
  { key: 'interment_time', from: 'interment_time' },
  { key: 'interment_postal_code', from: 'interment_postal_code' },
  { key: 'interment_lot', from: 'interment_lot' },
  { key: 'interment_section', from: 'interment_section' },
  { key: 'interment_block', from: 'interment_block' },
  { key: 'interment_county', from: 'interment_county' },
  { key: 'maternal_grandmother', from: 'maternal_grandmother' },
  { key: 'maternal_grandmothers_mother', from: 'maternal_grandmothers_mother' },
  { key: 'maternal_grandmothers_father', from: 'maternal_grandmothers_father' },
  { key: 'maternal_grandfather', from: 'maternal_grandfather' },
  { key: 'maternal_grandfathers_mother', from: 'maternal_grandfathers_mother' },
  { key: 'maternal_grandfathers_father', from: 'maternal_grandfathers_father' },
  { key: 'paternal_grandmother', from: 'paternal_grandmother' },
  { key: 'paternal_grandmothers_mother', from: 'paternal_grandmothers_mother' },
  { key: 'paternal_grandmothers_father', from: 'paternal_grandmothers_father' },
  { key: 'paternal_grandfather', from: 'paternal_grandfather' },
  { key: 'paternal_grandfathers_mother', from: 'paternal_grandfathers_mother' },
  { key: 'paternal_grandfathers_father', from: 'paternal_grandfathers_father' },
]

export const trimWrittenCaseValue = (value: string): string => value.trim()

export const SERVICE_AUTHOR_COLUMNS = [
  'location',
  'address',
  'city',
  'state',
  'postal_code',
  'date',
  'start_time',
  'end_time',
] as const

export type ServiceAuthorColumn = (typeof SERVICE_AUTHOR_COLUMNS)[number]

export type ServiceAuthorTarget = {
  serviceId: string
  column: ServiceAuthorColumn
}

const SERVICE_AUTHOR_COLUMN_SET: ReadonlySet<string> = new Set(
  SERVICE_AUTHOR_COLUMNS,
)

export const parseServiceAuthorColumn = (
  fieldKey: string,
): ServiceAuthorTarget | null => {
  const match = fieldKey.match(/^service_col:(.+):([a-z_]+)$/)
  if (!match) return null
  const [, serviceId, column] = match
  return SERVICE_AUTHOR_COLUMN_SET.has(column)
    ? { serviceId, column: column as ServiceAuthorColumn }
    : null
}

export const serviceBlockPartKey = (serviceId: string, part: string): string =>
  `service:${serviceId}:${part}`

export const serviceDefaultIdKey = (kind: 'service' | 'visitation'): string =>
  `service_default_id:${kind}`

export const FH_FIELD_SOURCES: readonly { key: string; from: string }[] = [
  { key: 'fh:name', from: 'name' },
  { key: 'fh:address', from: 'address' },
  { key: 'fh:address_2', from: 'address_2' },
  { key: 'fh:city', from: 'city' },
  { key: 'fh:state', from: 'state' },
  { key: 'fh:zip', from: 'zip_code' },
  { key: 'fh:phone', from: 'phone' },
  { key: 'fh:email', from: 'email_address' },
  { key: 'fh:website', from: 'website_url' },
]
